// src/services/api.js
import axios from 'axios';
import e from 'cors';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
});

let logoutCallback = null;

export const setLogoutCallback = (callback) => {
  logoutCallback = callback;
};

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response.status === 401 || error.response.status === 402) {
      console.log('Token expired, logging out...');
      if (logoutCallback) {
        logoutCallback();
      }
    }
    return Promise.reject(error);
  });

// server--------------------------------
export const getServerData = async () => {
  try {
    const response = await api.get('/server');
    return response.data;
  } catch (error) {
    console.error('Error fetching server data:', error);
    throw error;
  }
};

export const updateServerData = async (serverData) => {
  try {
    const response = await api.put('/server', serverData);
    return response.data;
  } catch (error) {
    console.error('Error updating server data:', error);
    throw error;
  }
};

// auth--------------------------------
export const login = async (email, password) => {
  try {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user_name', response.data.user_name);
      return response.data;
    }
    throw new Error('Login failed: No token received');
  } catch (error) {
    console.error('Login error:', error);
    if (error.response) {
      throw new Error(error.response.data.message || 'Login failed');
    } else if (error.request) {
      throw new Error('Network error: No response received');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

export const register = async (userData) => {
  try {
    const response = await api.post('/auth/register', { userData });
    return response.data;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

// dealer-------------------------------
export const getDealers = async () => {
  try {
    const response = await api.get(`/dealers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching dealers:', error);
    throw error;
  }
};

export const createDealer = async (dealerData) => {
  try {
    const response = await api.post(`/dealers`, dealerData);
    return response.data;
  } catch (error) {
    console.error('Error creating dealer:', error);
    throw error;
  }
};

export const updateDealer = async (dealerId, dealerData) => {
  try {
    const response = await api.put(`/dealers/${dealerId}`, dealerData);
    return response.data;
  } catch (error) {
    console.error('Error updating dealer:', error);
    throw error;
  }
};

export const deleteDealer = async (dealerId) => {
  try {
    await api.delete(`/dealers/${dealerId}`);
  } catch (error) {
    console.error('Error deleting dealer:', error);
    throw error;
  }
};

// users--------------------------------
export const getUsers = async () => {
  try {
    const response = await api.get(`/users`);
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    const response = await api.post(`/users`, userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    await api.delete(`/users/${userId}`);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// devices--------------------------------
export const getDevices = async () => {
  try {
    const response = await api.get(`/devices`);
    return response.data;
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
}

export const getDevicesByUserId = async (userId) => {
  try {
    const response = await api.get(`/devices/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
};

export const deleteDevice = async (deviceId) => {
  try {
    const response = await api.delete(`/devices/${deviceId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
}

//permissions--------------------------------
export const getUsersWithPermissions = async () => {
  try {
    const response = await api.get('/permissions/users/permissions');
    return response.data;
  } catch (error) {
    console.error('Error fetching users with permissions:', error);
    throw error;
  }
};

export const getUserPermissions = async (userId) => {
  try {
    const [userResponse, permissionsResponse] = await Promise.all([
      api.get(`/users/${userId}`),
      api.get(`/permissions/users/${userId}/permissions`)
    ]);
    return {
      ...userResponse.data,
      permissions: permissionsResponse.data
    };
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    throw error;
  }
};

export const updateUserPermissions = async (userId, permissions) => {
  try {
    const response = await api.put(`/permissions/users/${userId}/permissions`, permissions);
    return response.data;
  } catch (error) {
    console.error('Error updating user permissions:', error);
    throw error;
  }
};

export const deletePermissionsBundle = async (userId, permissionId, bundleId) => {
  try {
    await api.delete(`/permissions/users/${userId}/permissions/${permissionId}/bundles/${bundleId}`);
  } catch (error) {
    console.error('Error deleting permissions bundle:', error);
    throw error;
  }
};

export const getBundlesByPermissionId = async (userId,PermissionId) => {
  try {
    const response = await api.get(`/permissions/users/${userId}/${PermissionId}/bundles`);
    return response.data;
  } catch (error) {
    console.error('Error fetching bundles by permission:', error);
    throw error;
  }
};

export const createUserPermission = async (userId, permission) => {
  try {
    const response = await api.post(`/permissions/users/${userId}/permissions`, permission);
    return response.data;
  } catch (error) {
    console.error('Error creating user permission:', error);
    throw error;
  }
};

export const removeUserPermission = async (userId, permissionId) => {
  try {
    await api.delete(`/permissions/users/${userId}/permissions/${permissionId}`);
  } catch (error) {
    console.error('Error deleting user permission:', error);
    throw error;
  }
};

export const assignBundleToPermission = async (userId, permissionId, bundleData) => {
  try {
    const response = await api.post(`/permissions/assignBundleToPermission`, bundleData);
    return response.data;
  } catch (error) {
    console.error('Error assigning bundle to permission:', error);
    throw error;
  }
};

export const updateBundleAssignment = async (userId, permissionId, bundleData) => {
  try {
    const bundleId = bundleData.bundle_id;
    const response = await api.put(`/permissions/${bundleId}/updateBundleAssignment`, bundleData);
    return response.data;
  } catch (error) {
    console.error('Error updating bundle assignment:', error);
    throw error;
  }
}

// bundles--------------------------------
export const getBundles = async () => {
  try {
    const response = await api.get('/bundle');
    return response.data;
  } catch (error) {
    console.error('Error fetching bundles:', error);
    throw error;
  }
};

export const createBundle = async (bundleData) => {
  try {
    const response = await api.post('/bundle', bundleData);
    return response.data;
  } catch (error) {
    console.error('Error creating bundle:', error);
    throw error;
  }
};

export const updateBundle = async (bundleId, bundleData) => {
  try {
    const response = await api.put(`/bundle/${bundleId}`, bundleData);
    return response.data;
  } catch (error) {
    console.error('Error updating bundle:', error);
    throw error;
  }
};

export const deleteBundle = async (bundleId) => {
  try {
    await api.delete(`/bundle/${bundleId}`);
  } catch (error) {
    console.error('Error deleting bundle:', error);
    throw error;
  }
};

// assets--------------------------------
export const getAllAssets = async () => {
  try {
    const response = await api.get('/assets');
    return response.data;
  } catch (error) {
    console.error('Error fetching all assets:', error);
    throw error;
  }
};

export const getAssets = async () => {
  try {
    const response = await api.get('/assets');
    return response.data;
  } catch (error) {
    console.error('Error fetching assets:', error);
    throw error;
  }
};

export const createAsset = async (assetData) => {
  try {
    const response = await api.post('/assets', assetData);
    return response.data;
  } catch (error) {
    console.error('Error creating asset:', error);
    throw error;
  }
};

export const updateAsset = async (assetId, assetData) => {
  try {
    const response = await api.put(`/assets/${assetId}`, assetData);
    return response.data;
  } catch (error) {
    console.error('Error updating asset:', error);
    throw error;
  }
};

export const deleteAsset = async (assetId) => {
  try {
    await api.delete(`/assets/${assetId}`);
  } catch (error) {
    console.error('Error deleting asset:', error);
    throw error;
  }
};

// statistics----------------------------
export const getDownloadStatistics = async () => {
  try {
    const response = await api.get('/statistics');
    return response.data;
  } catch (error) {
    console.error('Error fetching all statistics:', error);
    throw error;
  }
};
export default api;